/**
 * Number, date and score formatting (spec §4.8: Swedish UI numbers use a
 * decimal comma). Pure functions — the language is always passed in.
 */
import { format as formatDateFns } from "date-fns";
import { enGB, sv as svLocale } from "date-fns/locale";
import { formatInTimeZone } from "date-fns-tz";
import type { Lang } from "@shared/api";
import { DEFAULT_TIMEZONE, startOfDayMs } from "@shared/time";

const LOCALE: Record<Lang, string> = { sv: "sv-SE", en: "en-GB" };
const DATE_LOCALE = { sv: svLocale, en: enGB } as const;

export interface NumberFormatOptions {
  /** Fixed number of decimals; omit for "up to `maxDecimals`". */
  decimals?: number;
  maxDecimals?: number;
}

/** 1234.5 → "1 234,5" (sv) / "1,234.5" (en). */
export function formatNumber(value: number, lang: Lang, options: NumberFormatOptions = {}): string {
  if (!Number.isFinite(value)) return "—";
  const { decimals, maxDecimals = 3 } = options;
  return new Intl.NumberFormat(LOCALE[lang], {
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? maxDecimals,
  }).format(value);
}

/** 0.732 → "73 %" (sv, with a non-breaking space) / "73%" (en). */
export function formatPercent(value: number, lang: Lang, decimals = 0): string {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(LOCALE[lang], {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** Exam-style points: "2,5 p" / "2.5 p" — half points are real (spec §3). */
export function formatPoints(value: number, lang: Lang): string {
  return `${formatNumber(value, lang, { maxDecimals: 1 })} p`;
}

/** ["2", "3", "5"] → "2, 3 och 5" (sv) / "2, 3 and 5" (en). */
export function formatList(items: readonly string[], lang: Lang): string {
  return new Intl.ListFormat(LOCALE[lang], { style: "long", type: "conjunction" }).format(items);
}

const SUPERSCRIPT: Record<string, string> = {
  "-": "⁻",
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
};

function superscript(exponent: number): string {
  return String(exponent)
    .split("")
    .map((ch) => SUPERSCRIPT[ch] ?? ch)
    .join("");
}

/**
 * How a parsed answer is echoed back to the student
 * ("tolkas som 6,63 × 10⁻³⁴"). Scientific notation outside 10⁻³ … 10⁵.
 */
export function formatScientific(value: number, lang: Lang, significantDigits = 4): string {
  if (!Number.isFinite(value)) return "—";
  if (value === 0) return "0";
  const magnitude = Math.abs(value);
  if (magnitude >= 1e-3 && magnitude < 1e5) {
    const rounded = Number(value.toPrecision(significantDigits));
    return formatNumber(rounded, lang, { maxDecimals: 6 });
  }
  const exponent = Math.floor(Math.log10(magnitude));
  const mantissa = Number((value / 10 ** exponent).toPrecision(significantDigits));
  return `${formatNumber(mantissa, lang, { maxDecimals: 6 })} × 10${superscript(exponent)}`;
}

/** Short date, e.g. "14 sep 2026" / "14 Sep 2026", in the app timezone. */
export function formatDate(ms: number, lang: Lang, timeZone: string = DEFAULT_TIMEZONE): string {
  return formatInTimeZone(new Date(ms), timeZone, "d MMM yyyy", { locale: DATE_LOCALE[lang] });
}

/**
 * A day key (`YYYY-MM-DD`) as weekday, day and month: "tors 25 sep" / "Thu 25 Sep". The key is
 * a calendar day in the app timezone, so it is read at that day's local midnight rather than as
 * UTC; "Coming back" and the course map's re-test date use it (pedagogy spec §3.3.5).
 */
export function formatDayShort(
  day: string,
  lang: Lang,
  timeZone: string = DEFAULT_TIMEZONE,
): string {
  const text = formatInTimeZone(new Date(startOfDayMs(day, timeZone)), timeZone, "EEE d MMM", {
    locale: DATE_LOCALE[lang],
  });
  // The Swedish locale abbreviates some months with a full stop ("sep."); a date ending a line of
  // UI text reads cleaner without it ("tors 25 sep"), as in the spec's own examples.
  return text.replace(/\.$/, "");
}

/** Date and time, e.g. "14 sep 2026 08:30". */
export function formatDateTime(
  ms: number,
  lang: Lang,
  timeZone: string = DEFAULT_TIMEZONE,
): string {
  return formatInTimeZone(new Date(ms), timeZone, "d MMM yyyy HH:mm", {
    locale: DATE_LOCALE[lang],
  });
}

/** A duration in ms as "H:MM:SS" — the exam timer. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/** "idag" / "today" style relative day label for activity lists. */
export function formatRelativeDay(ms: number, lang: Lang): string {
  const days = Math.round((Date.now() - ms) / 86_400_000);
  if (days <= 0) return lang === "sv" ? "idag" : "today";
  if (days === 1) return lang === "sv" ? "igår" : "yesterday";
  if (days < 7) return lang === "sv" ? `för ${days} dagar sedan` : `${days} days ago`;
  return formatDateFns(new Date(ms), "d MMM yyyy", { locale: DATE_LOCALE[lang] });
}
